import React, { useEffect, useState } from 'react'
import { startRegistration } from '@simplewebauthn/browser'
import { FaKey, FaMobileAlt, FaPlus } from 'react-icons/fa'
import { Box, Stack, FormControl, Input, Heading, Text, Button, SimplePopover, Alert, ReadOnlyCopyInput, Icon, SimpleGrid, Card, Badge } from '@/components/lib'
import QrCode from '@/components/includes/QrCode'
import utils from '../../utils/utils'
import api from '../../api'
import useStore from '../../store'

function AccountPassword () {
  const [existingPassword, setExistingPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [otpUri, setOtpUri] = useState('')
  const [otpSecret, setOtpSecret] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [disableOtpPassword, setDisableOtpPassword] = useState('')
  const [passkeys, setPasskeys] = useState([])
  const [passkeyName, setPasskeyName] = useState('')
  const [registeringPasskey, setRegisteringPasskey] = useState(false)
  const [deletePasskeyPassword, setDeletePasskeyPassword] = useState('')

  const { user, updateUser } = useStore()

  const refreshPasskeys = () => {
    api.accounts.listPasskeys(data => setPasskeys(data.passkeys || []), () => {})
  }

  useEffect(() => {
    if (user?.hasPassword) refreshPasskeys()
  }, [user?.hasPassword])

  const updatePassword = () => {
    api.accounts.updatePassword(existingPassword, newPassword, () => {
      setExistingPassword('')
      setNewPassword('')
      utils.toast('success', 'Password updated')
    }, err => utils.toast('error', 'Unable to update your password', err.message))
  }

  const getOtpUri = () => {
    api.accounts.getOtpUri(data => {
      setOtpUri(data.uri)
      setOtpSecret(data.secret)
    }, err => utils.toast('error', err.message))
  }
  const enableMfa = () => {
    api.accounts.enableMfa(otpCode, data => {
      updateUser(user._id, { otpEnabled: true })
    }, err => utils.toast('error', err.message))
  }
  const disableMfa = () => {
    api.accounts.disableMfa(disableOtpPassword, data => {
      updateUser(user._id, { otpEnabled: false })
    }, err => utils.toast('error', err.message))
  }

  const addPasskey = async () => {
    const name = passkeyName.trim()
    if (!name) {
      utils.toast('error', 'Please give your passkey a name first')
      return
    }
    setRegisteringPasskey(true)
    api.accounts.getPasskeyRegistrationOptions(async options => {
      try {
        const credential = await startRegistration({ optionsJSON: options })
        api.accounts.registerPasskey(credential, name, data => {
          setRegisteringPasskey(false)
          setPasskeyName('')
          updateUser(user._id, { hasPasskey: true })
          refreshPasskeys()
          utils.toast('success', 'Passkey added')
        }, err => {
          setRegisteringPasskey(false)
          utils.toast('error', 'Unable to add your passkey', err.message)
        })
      } catch (err) {
        setRegisteringPasskey(false)
        if (err?.name !== 'NotAllowedError' && err?.name !== 'AbortError') {
          utils.toast('error', 'Unable to add your passkey', err.message)
        }
      }
    }, err => {
      setRegisteringPasskey(false)
      utils.toast('error', err.message)
    })
  }

  const removePasskey = (passkeyId) => {
    api.accounts.deletePasskey(passkeyId, deletePasskeyPassword, data => {
      setDeletePasskeyPassword('')
      const remaining = passkeys.filter(p => p._id !== passkeyId)
      setPasskeys(remaining)
      if (remaining.length === 0) updateUser(user._id, { hasPasskey: false })
      utils.toast('success', 'Passkey removed')
    }, err => utils.toast('error', 'Unable to remove your passkey', err.message))
  }

  return (
    <Stack gap={5}>
      <Stack rounded='lg' borderWidth='1px' p={3} gap={3}>
        <Box>
          <Stack direction='row' alignItems='center'>
            <Heading size='md' as='h3'>Change account password</Heading>
            <Badge colorPalette='blue'>Login</Badge>
          </Stack>
          <Text>Update your account password to one that is strong and hard to guess.</Text>
        </Box>

        {user?.hasPassword
          ? (
            <Stack>
              <FormControl label='Current password'>
                <Input type='password' value={existingPassword} onChange={e => setExistingPassword(e.target.value)} />
              </FormControl>

              <FormControl label='New password'>
                <Input label='New password' type='password' value={newPassword} onChange={e => setNewPassword(e.target.value)} />
              </FormControl>
              <Box><Button colorPalette='primary' onClick={updatePassword}>Update password</Button></Box>
            </Stack>)
          : (
            <Alert status='warning'>
              Your account password is managed by your organisation. You can't change it here.
            </Alert>)}
      </Stack>

      {user?.hasPassword
        ? (
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
            <Stack rounded='lg' borderWidth='1px' p={3} gap={3}>
              <Stack direction='row' alignItems='center' gap={3}>
                <Icon as={FaMobileAlt} boxSize={6} />
                <Heading size='md' as='h3'>Authenticator app</Heading>
                <Badge colorPalette='blue'>2FA</Badge>
              </Stack>
              <Text>Use a one-time code generated by an authenticator app each time you sign in.</Text>
              {user?.otpEnabled
                ? (
                  <Stack>
                    <Alert status='success' variant='left-accent' title='Enabled'>
                      You will be asked for a temporary code generated by your 2FA app each time you login.
                    </Alert>
                    <Box>
                      <SimplePopover
                        trigger={<Button colorPalette='red'>Turn off 2FA</Button>}
                        header={
                          <>
                            <Heading size='md' as='h4'>Turn off 2FA for your account</Heading>
                            <Text>We recommend keeping 2FA turned on for your account, but if you want to turn it off (or re-enable it), enter your account password below to confirm.</Text>
                          </>
                        }
                        body={
                          <Input type='password' placeholder='Your password' value={disableOtpPassword} onChange={e => setDisableOtpPassword(e.target.value)} />
                        }
                        footer={
                          <Button colorPalette='red' onClick={disableMfa}>Turn off 2FA</Button>
                        }
                      />
                    </Box>
                  </Stack>)
                : (
                  <Box>
                    <SimplePopover
                      trigger={<Button colorPalette='primary' onClick={getOtpUri}><Icon as={FaPlus} mr={1} /> Set-up 2FA</Button>}
                      header={
                        <>
                          <Heading size='md' as='h4'>Set-up 2FA</Heading>
                        </>
                      }
                      body={
                        <Stack gap={5}>
                          <Stack bg='bg.surface' p={3} rounded='md'>
                            <Heading size='sm' as='h5'>1. Configure your 2FA app or device</Heading>
                            <Text>Scan the QR code below using your 2FA app.</Text>
                            <QrCode name='Trialflare' url={otpUri} size={200} />
                            <Text>If your app or device can't read the QR code, you can use the following text code instead:</Text>
                            <ReadOnlyCopyInput value={otpSecret} size='sm' />
                          </Stack>
                          <Stack bg='bg.surface' p={3} rounded='md'>
                            <Heading size='sm' as='h5'>2. Verify your set-up</Heading>
                            <Text>Now, enter the six-digit code generated by your 2FA app or device to verify the set-up.</Text>
                            <Input autoFocus placeholder='123456' value={otpCode} onChange={e => setOtpCode(e.target.value)} />
                          </Stack>

                          <Button colorPalette='primary' onClick={enableMfa}>Complete setup</Button>
                        </Stack>
                      }
                    />
                  </Box>)}
            </Stack>

            <Stack rounded='lg' borderWidth='1px' p={3} gap={3}>
              <Stack direction='row' alignItems='center' gap={3}>
                <Icon as={FaKey} boxSize={6} />
                <Heading size='md' as='h3'>Passkeys</Heading>
                <Badge colorPalette='blue'>Login</Badge>
                <Badge colorPalette='blue'>2FA</Badge>
              </Stack>
              <Text>Sign in using your device's biometrics, screen lock, or a hardware security key. Passkeys can be used as a second factor or to sign in without a password.</Text>

              {passkeys.length > 0 &&
                <Stack gap={2}>
                  {passkeys.map(p => (
                    <Card key={p._id}>
                      <Stack direction='row' alignItems='center' gap={3}>
                        <Icon as={FaKey} />
                        <Stack gap={1}>
                          <Text fontWeight='semibold'>{p.name}</Text>
                          <Text fontSize='sm' color='fg.muted'>
                            Added {utils.formatDateTime(p.createdAt)}
                            {p.lastUsedAt ? ` · Last used ${utils.formatDateTime(p.lastUsedAt)}` : ' · Never used'}
                          </Text>
                          <Box>
                            <SimplePopover
                              trigger={<Button size='xs' variant='ghost'>Remove passkey</Button>}
                              header={
                                <>
                                  <Heading size='md' as='h4'>Remove this passkey</Heading>
                                  <Text>Enter your account password to confirm removing "{p.name}".</Text>
                                </>
                              }
                              body={
                                <Input type='password' placeholder='Your password' value={deletePasskeyPassword} onChange={e => setDeletePasskeyPassword(e.target.value)} />
                              }
                              footer={
                                <Button colorPalette='red' onClick={() => removePasskey(p._id)}>Remove passkey</Button>
                              }
                            />
                          </Box>
                        </Stack>
                      </Stack>
                    </Card>
                  ))}
                </Stack>}

              <Box>
                <SimplePopover
                  trigger={<Button colorPalette='primary'><Icon as={FaPlus} mr={1} /> Add a passkey</Button>}
                  header={
                    <>
                      <Heading size='md' as='h4'>Add a passkey</Heading>
                      <Text>Give this passkey a name so you can recognise it later.</Text>
                    </>
                  }
                  body={
                    <FormControl label='Name for this passkey' helperText='A device identifier, such as "Apple Keychain", "YubiKey", etc.'>
                      <Input autoFocus value={passkeyName} placeholder='My passkey device name...' onChange={e => setPasskeyName(e.target.value)} />
                    </FormControl>
                  }
                  footer={
                    <Button colorPalette='primary' onClick={addPasskey} isLoading={registeringPasskey}>Create</Button>
                  }
                />
              </Box>
            </Stack>
          </SimpleGrid>)
        : (
          <Alert
            status='warning'
            title={'Your account password is managed by your organisation. You can\'t set up two-factor authentication or passkeys here.'}
          />)}
    </Stack>
  )
}

export default AccountPassword
